import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SVGAssets } from '@business/assets/vectors';
import {
  getFileSizeString,
  handlePhotoClick
} from '@utils/helpers/lib/photoHelpers';
import { useLocation, useNavigate } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { getMonthDateFormat } from '@utils/helpers/lib';
import {
  setSelectedMedia,
  getSwitchedProfileUserId
} from '@ports/common/commonStateSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { usePhotoContextMenu } from '@hooks/usePhotoContextMenu';
import { useTrashContextMenu } from '@hooks/useTrashContextMenu';
import { useTrashBulkOperations } from '@hooks/TrashContextMenu';
import {
  useAssetSearchMutation,
  useBulkDeleteAssetsMutation,
  useDeleteAssetsMutation,
  useMergeAssetMutation
} from '@ports/internal-services/asset/assetSlice';
import { useBulkCreatePrivateMutation } from '@ports/internal-services/private/privateSlice';
import { useBulkRestoreAssetsMutation } from '@ports/internal-services/trash/trashSlice';
import {
  getUserNotificationData,
  getPhotoFilterOptions,
  setPhotoFilterOptions,
  getFolderFilterOptions,
  setFolderFilterOptions,
  incrementAssetRefreshCounter,
  getAssetRefreshCounter,
  getIsPrivateDataVisible
} from '@ports/common/sessionStateSlice';
import MovePhotoModal from '@business/workflows/ComponentViews/Photos/MovePhotoModal';
import MediaInfoSidebar from '@business/workflows/ComponentViews/Photos/MediaInfoSidebar';
import { useRemoveAssetsFromFolderMutation } from '@ports/internal-services/folder/folderSlice';
import { useDebounce } from '@hooks/Debounce';
import { PhotosListProps } from '@business/interfaces/photo';
import AddDiaryModal from '@business/workflows/ComponentViews/Photos/AddDiaryModal';
import { ShareModalComponent } from '@business/workflows/ComponentUI/Share';
import { getFavoriteViewData } from '@ports/common/favoriteViewSlice';
import { getPermissions } from '@ports/common/authStateSlice';
import { getFolderDetails } from '@ports/common/folderStateSlice';
import { Pagination } from '@business/workflows/ComponentUI/Pagination';
import { ConfirmationModal } from '../../ConfirmationModal';
import { ConfirmationBox } from '../../ConfirmationBox';
import { showToast } from '../../Toast/Toast';
import { SelectionBar } from '../../SelectionBar';
import { ContextMenu } from '../../ContextMenu';
import { ContextMenuItem } from '../../ContextMenu/ContextMenuItem';
import { FilterSearchEmpty } from '../../FilterSearchEmpty';
import { Table } from '../../Table';
import { PhotoGrid } from '../PhotoGrid';
import { SearchInput } from '../../HierarchicalMultiSelect/search';
import { Filters } from '../../Filters';
import { Button } from '../../Button';
import { AppLoader } from '../../AppLoader';
import { NameCellRenderer } from './NameCellRenderer';
import { PrivatePublicFilter } from './PrivatePublicFilter';

export const PhotosList: FC<PhotosListProps> = ({
  setSelectedPhotos,
  selectedPhotos,
  setIsDeleteAllowed,
  setSelectedFolders = () => {},
  selectedFolders = [],
  isFilterEnabled = false,
  isEnableAdd = false,
  initialFilterOptions = {},
  initialSelectedView = 'grid',
  handleAddMedia = () => {},
  emptyStateComponent,
  isFavorites,
  filterSelectedView,
  isWhoIsThis,
  refreshTrigger,
  isPending = false,
  isFromMomentDetails,
  hideContextMenu = false,
  hidePrivatePublicFilter = false,
  additionalSelectionActions,
  isFromPeopleDetail,
  isMyFiles,
  isSharedWithMe,
  showFavoriteWhenContextMenuHidden = false,
  groupId,
  isContributor,
  isExcludeFromFilter,
  module
  // isBackEnabled = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const folderDetails = useSelector(getFolderDetails);
  const isPhotosView = useSelector(getFavoriteViewData)?.isPhotosView;
  const userNotificationData = useSelector(getUserNotificationData);
  const permissions = useSelector(getPermissions);
  const canMerge = permissions?.includes('sharing:merge') ?? false;
  const canReject = permissions?.includes('sharing:reject') ?? false;
  const savedFilterOptions = useSelector(getPhotoFilterOptions);
  const savedFolderFilterOptions = useSelector(getFolderFilterOptions);
  const switchedProfileUserId = useSelector(getSwitchedProfileUserId);
  const isPrivateDataVisible = useSelector(getIsPrivateDataVisible) ?? true;
  const assetRefreshCounter = useSelector(getAssetRefreshCounter);
  const currentUserId = useSelector(getUserData)?.userId;
  const [photos, setPhotos] = useState<any>(null);

  // Fetch group permissions if in group flow
  // const { data: groupPermissionsData } = useGetGroupPermissionsQuery(
  //   { groupId: groupId || '' },
  //   { skip: !groupId }
  // );

  // Determine if user has owner access in group flow
  // const isOwnerAccess = !groupId
  //   ? true
  //   : (groupPermissionsData?.data?.includes('owner_access:true') ?? false);
  const [photosCount, setPhotosCount] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [selectedView, setSelectedView] = useState<'grid' | 'list'>(
    initialSelectedView
  );
  const [isBulkDeleteConfirm, setIsBulkDeleteConfirm] =
    useState<boolean>(false);
  const [isBulkPrivateConfirm, setIsBulkPrivateConfirm] =
    useState<boolean>(false);
  const [isMergeConfirmOpen, setIsMergeConfirmOpen] = useState<boolean>(false);
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] =
    useState<boolean>(false);
  const [isMerging, setIsMerging] = useState<boolean>(false);
  const [isRejecting, setIsRejecting] = useState<boolean>(false);
  const [isBulkMoveModalOpen, setIsBulkMoveModalOpen] =
    useState<boolean>(false);
  const [isBulkShareModalOpen, setIsBulkShareModalOpen] =
    useState<boolean>(false);
  const [isUpdateFilters, setIsUpdateFilters] = useState<boolean>(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);

  // Determine if we're in a folder context
  const isInFolderContext = !!initialFilterOptions?.folderId;

  // Determine if we're in a moment context
  const isInMomentContext = !!initialFilterOptions?.momentId;

  // Track if initial filters have been properly applied
  const hasInitialFiltersApplied = useRef(false);
  const initialFilterKeys = useRef(Object.keys(initialFilterOptions || {}));
  const location = useLocation();
  const source = new URLSearchParams(location.search).get('source');

  const [filterOptions, setFilterOptions] = useState<any>(() => {
    const defaultFilters = {
      search: '',
      accessType: 'public',
      ...initialFilterOptions
    };

    // If initialFilterOptions has a folderId, use folder photo filters
    if (initialFilterOptions?.folderId) {
      return savedFolderFilterOptions?.folderId ===
        initialFilterOptions.folderId
        ? savedFolderFilterOptions
        : defaultFilters;
    }

    // If initialFilterOptions has a momentId, use the initial options directly
    // (don't use saved filters for moments as they're context-specific)
    if (initialFilterOptions?.momentId) {
      return defaultFilters;
    }

    if (savedFilterOptions) {
      return {
        ...savedFilterOptions,
        ...initialFilterOptions // initialFilterOptions should override savedFilterOptions
      };
    }

    return defaultFilters;
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBulkRemoveFromFolderModalOpen, setIsBulkRemoveFromFolderModalOpen] =
    useState<boolean>(false);

  const [DeleteAsset] = useDeleteAssetsMutation();
  const [AssetSearch] = useAssetSearchMutation();
  const [BulkDeleteAssets] = useBulkDeleteAssetsMutation();
  const [BulkCreatePrivate] = useBulkCreatePrivateMutation();
  const [mergeAsset] = useMergeAssetMutation();
  const [RestoreModule] = useBulkRestoreAssetsMutation();
  const [RemoveFromFolder] = useRemoveAssetsFromFolderMutation();

  const folderId = initialFilterOptions?.folderId;
  const [isRemoveFromFolderModalOpen, setIsRemoveFromFolderModalOpen] =
    useState<boolean>(false);
  const [assetToRemoveFromFolder, setAssetToRemoveFromFolder] =
    useState<string>('');

  const debouncedSearch = useDebounce(search, 500);
  const isSharedWithMeSource = source?.toLowerCase?.() === 'sharedwithme';
  useEffect(() => {
    const filterOptionsWithoutFolderId = { ...filterOptions };
    delete filterOptionsWithoutFolderId.folderId;
    delete filterOptionsWithoutFolderId.momentId;

    if (isInFolderContext) {
      // In folder context, update folder photo filters with folderId
      dispatch(setFolderFilterOptions(filterOptions));
    } else if (isInMomentContext) {
      // In moment context, don't persist filters to avoid conflicts
      // Filters are managed by the parent MomentDetails component
    } else {
      // Not in folder or moment context, update global photo filters
      dispatch(setPhotoFilterOptions(filterOptionsWithoutFolderId));
    }
  }, [filterOptions, dispatch, isInFolderContext, isInMomentContext]);

  useEffect(() => {
    assetFiltersHandler();
  }, [currentPage, selectedView]);

  // Reset to page 1 when filters change or view changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [filterOptions]);

  const assetFiltersHandler = useCallback(
    (assetId?: string) => {
      // Handle sortBy logic
      let sortBy = 'takenDate';
      let orderBy = 'desc'; // default to most recent

      if (filterOptions?.sortBy) {
        const sortValue = filterOptions.sortBy;
        if (sortValue === 'mostRecent') {
          sortBy = 'takenDate';
          orderBy = 'desc';
        } else if (sortValue === 'leastRecent') {
          sortBy = 'takenDate';
          orderBy = 'asc';
        }
      }

      const offset =
        selectedView === 'list' ? (currentPage - 1) * pageSize : undefined;
      const limit = selectedView === 'list' ? pageSize : undefined;

      const dataObj = {
        assetSearch: {
          ...filterOptions,
          search: filterOptions?.search,
          sortBy: sortBy,
          orderBy: orderBy,
          favorites: filterOptions?.favorites || false,
          sharedWith: filterOptions?.sharedWith || '',
          people: filterOptions?.people || '',
          things: filterOptions?.things || '',
          locations: filterOptions?.locations || '',
          dates: filterOptions?.date || '',
          interactions: filterOptions?.interactions || '',
          mediaType:
            filterOptions?.mediaType?.length > 0
              ? filterOptions?.mediaType
              : undefined,
          access: filterOptions?.accessType === 'public' ? 'public' : 'private',
          assetId,
          offset: isFavorites
            ? isPhotosView
              ? undefined
              : 0
            : selectedView === 'list'
              ? offset
              : undefined,
          limit: isFavorites
            ? isPhotosView
              ? undefined
              : 5
            : selectedView === 'list'
              ? limit
              : undefined,
          ...(switchedProfileUserId ? { ownerId: switchedProfileUserId } : {}),
          isTrash: folderDetails?.isFromTrash ? true : undefined,
          isInsidePeople: isFromPeopleDetail,
          isSharedWithMe: isSharedWithMe ? true : undefined,
          isMyFiles: isMyFiles ? true : undefined,
          isContributor: isContributor ? true : undefined
        }
      };
      if (!assetId) {
        setIsLoading(true);
      }
      AssetSearch(dataObj)
        .then((res: any) => {
          if (res?.data) {
            if (assetId) {
              let countIncrement = 1;
              setPhotos((prev: any) => {
                if (!prev) return res?.data?.data;
                const itemIndex = res?.data?.data?.rows?.findIndex(
                  (a: any) => a.id === assetId
                );
                const updatedAsset = {
                  ...prev,
                  rows: [...prev.rows],
                  count: prev.count
                };
                const updatedItemIndex = updatedAsset?.rows?.findIndex(
                  (a: any) => a.id === assetId
                );
                if (itemIndex !== -1) {
                  const updatedItem = res?.data?.data?.rows?.[itemIndex];
                  if (updatedItemIndex === -1) {
                    if (orderBy === 'desc') {
                      updatedAsset.rows.unshift(updatedItem);
                    } else {
                      updatedAsset.rows.push(updatedItem);
                    }
                    updatedAsset.count += 1;
                  } else {
                    updatedAsset.rows.splice(updatedItemIndex, 1, updatedItem);
                  }
                  return updatedAsset;
                } else {
                  if (updatedItemIndex !== -1) {
                    // If the updated asset is not in the current list, remove it from the results
                    updatedAsset.rows.splice(updatedItemIndex, 1);
                    updatedAsset.count -= 1;
                    countIncrement = -1;
                    return updatedAsset;
                  }
                }

                return prev;
              });
              setPhotosCount(prev => prev + countIncrement);
            } else {
              setPhotos(res?.data?.data);
              setPhotosCount(res?.data?.data?.totalCount);
            }
            setTotalCount(res?.data?.data?.count || 0);
          } else {
            showToast({
              type: 'error',
              isAlert: true,
              alertProps: {
                type: 'error',
                message: t('error', { module: t('photos') })
              }
            });
          }
        })
        .catch(err => {
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: err?.message
            }
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      filterOptions,
      isFromMomentDetails,
      isFromPeopleDetail,
      isMyFiles,
      isSharedWithMe,
      switchedProfileUserId,
      folderDetails,
      currentPage,
      selectedView
    ]
  );

  // Use photo context menu for normal mode
  const photoContextMenu = usePhotoContextMenu({
    assetFiltersHandler
  });

  // Use trash context menu for trash mode
  const trashContextMenu = useTrashContextMenu();

  // Determine if we're in trash mode
  const isTrashMode = folderDetails?.isFromTrash;

  // Use trash bulk operations hook for trash mode
  const trashBulkOperations = useTrashBulkOperations({
    selectedPhotos: isTrashMode ? selectedPhotos : [],
    selectedPhotoFolders: isTrashMode ? selectedFolders : [],
    onClearSelections: () => {
      setSelectedPhotos([]);
      setSelectedFolders([]);
    },
    onRefetchData: () => {
      assetFiltersHandler();
    }
  });

  // Conditionally use the appropriate context menu based on trash mode
  const {
    assetContextMenuItems,
    isDeleteAssetConfirm,
    selectedAsset,
    setIsDeleteAssetConfirm,
    setSelectedAsset
  } = isTrashMode
    ? {
        assetContextMenuItems: trashContextMenu.assetContextMenuItems,
        isDeleteAssetConfirm: trashContextMenu.isDeleteAssetConfirm,
        selectedAsset: trashContextMenu.selectedAsset,
        setIsDeleteAssetConfirm: trashContextMenu.setIsDeleteAssetConfirm,
        setSelectedAsset: trashContextMenu.setSelectedAsset
      }
    : {
        assetContextMenuItems: photoContextMenu.assetContextMenuItems,
        isDeleteAssetConfirm: photoContextMenu.isDeleteAssetConfirm,
        selectedAsset: photoContextMenu.selectedAsset,
        setIsDeleteAssetConfirm: photoContextMenu.setIsDeleteAssetConfirm,
        setSelectedAsset: photoContextMenu.setSelectedAsset
      };

  // These features are only available in non-trash mode
  const {
    isInfoSidebarOpen,
    isMovePhotoModalOpen,
    isAddDiaryModalOpen,
    setIsAddDiaryModalOpen,
    setIsMovePhotoModalOpen,
    setIsInfoSidebarOpen,
    handleToggleFavorite,
    isShareModalOpen,
    setIsShareModalOpen
  } = photoContextMenu;

  // Trash-specific states (only used in trash mode)
  const {
    restoreAssetId,
    setRestoreAssetId,
    isAssetRestoreConfirm,
    setIsAssetRestoreConfirm
  } = trashContextMenu;

  useEffect(() => {
    if (
      userNotificationData?.page === 'photos' &&
      userNotificationData?.notificationData?.assetId
    ) {
      assetFiltersHandler(userNotificationData?.notificationData?.assetId);
    }
  }, [userNotificationData]);

  // Centralized effect to handle assetFiltersHandler call
  useEffect(() => {
    // If we have initialFilterOptions with specific keys (like 'people', 'folderId', 'momentId'),
    // wait until those filters are properly applied before calling the API
    if (
      initialFilterKeys.current.length > 0 &&
      !hasInitialFiltersApplied.current
    ) {
      // Determine which keys are truly required (others can be empty)
      const requiredKeys: string[] = [];
      if (isInFolderContext) requiredKeys.push('folderId');
      if (isInMomentContext) requiredKeys.push('momentId');

      // Only check required keys, not all filter keys
      const hasRequiredFilters =
        requiredKeys.length === 0 ||
        requiredKeys.every(
          key => filterOptions?.[key] !== undefined && filterOptions[key] !== ''
        );

      if (!hasRequiredFilters) {
        // Skip API call until required filters are present
        return;
      }

      hasInitialFiltersApplied.current = true;
    }

    setSelectedPhotos([]);
    setSelectedFolders([]);
    assetFiltersHandler();
  }, [filterOptions, isInFolderContext, isInMomentContext]);

  // Centralized effect to handle assetFiltersHandler call
  useEffect(() => {
    if (initialFilterOptions && Object.keys(initialFilterOptions).length > 0) {
      // Update the tracked initial filter keys
      initialFilterKeys.current = Object.keys(initialFilterOptions);
      hasInitialFiltersApplied.current = false;

      setFilterOptions((prev: any) => {
        // Ensure initialFilterOptions always takes precedence over previous state
        const updated = {
          ...prev,
          ...initialFilterOptions
        };

        // Check if there's an actual change to avoid unnecessary re-renders
        const hasChanged = Object.keys(initialFilterOptions).some(
          key => prev[key] !== initialFilterOptions[key]
        );

        return hasChanged ? updated : prev;
      });
    }
  }, [initialFilterOptions]);

  // Update selectedView when filterSelectedView changes (for favorites page)
  useEffect(() => {
    if (filterSelectedView) {
      setSelectedView(filterSelectedView);
    }
  }, [filterSelectedView]);

  useEffect(() => {
    setSelectedView(initialSelectedView);
  }, [initialSelectedView]);

  useEffect(() => {
    setFilterOptions((prev: any) => {
      if (prev.search === debouncedSearch) {
        return prev;
      }
      return {
        ...prev,
        search: debouncedSearch
      };
    });
  }, [debouncedSearch]);

  // Refresh when parent component triggers refresh
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      assetFiltersHandler();
    }
  }, [refreshTrigger]);

  // Refresh when assets change elsewhere (e.g. assets added via "select from gallery")
  useEffect(() => {
    if (assetRefreshCounter > 0) {
      assetFiltersHandler();
    }
  }, [assetRefreshCounter]);

  // Refetch when switched profile changes
  useEffect(() => {
    assetFiltersHandler();
  }, [switchedProfileUserId]);

  const photoArray = useMemo(() => {
    return photos?.rows || [];
  }, [photos]);

  const handleSelectAll = () => {
    if (selectedPhotos.length === photosCount) {
      setSelectedPhotos([]);
    } else {
      setSelectedPhotos(photos?.rows.map((p: any) => p.id));
    }
  };

  const handlePhotoSelect = useCallback(
    (photoId: string) => {
      setSelectedPhotos(prev => {
        const newSelected = prev.includes(photoId)
          ? prev.filter(id => id !== photoId)
          : [...prev, photoId];
        if (typeof setIsDeleteAllowed === 'function') {
          setIsDeleteAllowed(
            !photoArray.some(
              (photo: any) =>
                newSelected.includes(photo.id) && photo.status === 'Active'
            )
          );
        }
        return newSelected;
      });
      // Close folder selection when selecting photos
      if (selectedFolders.length > 0) {
        setSelectedFolders([]);
      }
    },
    [selectedFolders, photoArray]
  );

  const handleRowClick = (photo: any) => {
    handlePhotoClick(
      photo,
      navigate,
      dispatch,
      setSelectedMedia,
      routePaths.MediaView,
      'default',
      groupId
    );
  };

  const deleteAssetHandler = async (assetId: string) => {
    try {
      await DeleteAsset({ id: assetId }).unwrap();
      dispatch(incrementAssetRefreshCounter());
      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: t('photosPage.assetDeleteSuccess')
        }
      });
      assetFiltersHandler();
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t('photosPage.assetDeleteError')
        }
      });
    } finally {
      setSelectedAsset(null);
    }
  };

  const restoreAssetHandler = (assetId: string) => {
    const dataObj = {
      bulkRestoreTrash: {
        module: 'assets',
        moduleId: [assetId]
      }
    };
    RestoreModule(dataObj)
      .then(() => {
        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: t('assetRecover')
          }
        });
        setIsAssetRestoreConfirm(false);
        setRestoreAssetId('');
        assetFiltersHandler();
      })
      .catch(err => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: err?.message || t('assetRecoverError')
          }
        });
        setIsAssetRestoreConfirm(false);
      });
  };

  const handleMergeAssets = () => {
    if (selectedPhotos.length === 0) return;
    setIsMergeConfirmOpen(true);
  };

  const handleRejectAssets = () => {
    if (selectedPhotos.length === 0) return;
    setIsRejectConfirmOpen(true);
  };

  // const handlePendingMerge = useCallback((photoId: string) => {
  //   setSelectedPhotos([photoId]);
  //   setIsMergeConfirmOpen(true);
  // }, []);

  // const handlePendingReject = useCallback((photoId: string) => {
  //   setSelectedPhotos([photoId]);
  //   setIsRejectConfirmOpen(true);
  // }, []);

  const handleMergeConfirm = () => {
    setIsMergeConfirmOpen(false);
    setIsMerging(true);
    mergeAsset({
      type: 'asset',
      mergeAssetBody: { assetIds: selectedPhotos }
    } as any)
      .then((res: any) => {
        if (res?.data?.ok) {
          showToast({
            type: 'success',
            isAlert: true,
            alertProps: {
              type: 'success',
              message: 'Assets merged successfully.'
            }
          });
          setSelectedPhotos([]);
          assetFiltersHandler();
        } else {
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: { type: 'error', message: 'Failed to merge assets.' }
          });
        }
      })
      .catch(() => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: { type: 'error', message: 'Failed to merge assets.' }
        });
      })
      .finally(() => setIsMerging(false));
  };

  const handleRejectConfirm = () => {
    setIsRejectConfirmOpen(false);
    setIsRejecting(true);
    mergeAsset({
      type: 'asset',
      mergeAssetBody: { assetIds: selectedPhotos, isReject: true }
    } as any)
      .then((res: any) => {
        if (res?.data?.ok) {
          showToast({
            type: 'success',
            isAlert: true,
            alertProps: {
              type: 'success',
              message: 'Assets rejected successfully.'
            }
          });
          setSelectedPhotos([]);
          assetFiltersHandler();
        } else {
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: { type: 'error', message: 'Failed to reject assets.' }
          });
        }
      })
      .catch(() => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: { type: 'error', message: 'Failed to reject assets.' }
        });
      })
      .finally(() => setIsRejecting(false));
  };

  const handleBulkDelete = () => {
    if (selectedPhotos.length === 0) return;
    setIsBulkDeleteConfirm(true);
  };

  const removeFromFolderHandler = (assetId: string) => {
    const dataObj = {
      folderId: folderId,
      body: {
        assetIds: [assetId]
      }
    };
    RemoveFromFolder(dataObj)
      .then(() => {
        setIsRemoveFromFolderModalOpen(false);
        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: t('photosPage.moveSuccess')
          }
        });
        assetFiltersHandler();
      })
      .catch((err: any) => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: err?.message || t('photosPage.moveError')
          }
        });
      });
  };

  const confirmBulkDelete = async () => {
    try {
      await BulkDeleteAssets({
        body: {
          assetIds: selectedPhotos
        }
      }).unwrap();
      dispatch(incrementAssetRefreshCounter());
      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: t('photosPage.bulkDeleteSuccess')
        }
      });
      setSelectedPhotos([]);
      setIsBulkDeleteConfirm(false);
      assetFiltersHandler();
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t('photosPage.bulkDeleteError')
        }
      });
      setIsBulkDeleteConfirm(false);
    }
  };

  const handleBulkPrivate = () => {
    if (selectedPhotos.length === 0) return;
    setIsBulkPrivateConfirm(true);
  };

  const confirmBulkPrivate = async () => {
    await BulkCreatePrivate({
      body: {
        assetIds: selectedPhotos
      }
    })
      .then((res: any) => {
        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: t(`messageCode.${res?.data?.data?.code}`)
          }
        });
        dispatch(incrementAssetRefreshCounter());
        setSelectedPhotos([]);
        setIsBulkPrivateConfirm(false);
        assetFiltersHandler();
      })
      .catch((err: any) => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: err?.message || t('photosPage.bulkPrivateError')
          }
        });
        setIsBulkPrivateConfirm(false);
      });
  };

  const columns = useMemo(() => {
    return [
      {
        header: t('name'),
        accessorKey: 'fileName',
        cell: ({ row }: any) => {
          const photo = row.original;
          const isChecked = selectedPhotos.includes(photo?.id);

          return (
            <NameCellRenderer
              photo={photo}
              isChecked={isChecked}
              handlePhotoSelect={handlePhotoSelect}
              handleToggleFavorite={handleToggleFavorite}
              hideFavorite={hideContextMenu}
            />
          );
        }
      },
      {
        header: t('dateCreated'),
        accessorKey: 'takenDate',
        cell: ({ getValue }: any) => {
          const value = getValue();
          if (!value) return <span className="text-sm text-gray-500">-</span>;
          const date = new Date(value);
          return (
            <span className="text-sm text-gray-500">
              {date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
            </span>
          );
        }
      },
      {
        header: t('interactions'),
        accessorKey: 'interactions',
        cell: ({ row }: any) => {
          return (
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center space-x-1">
                <SVGAssets.LikeIcons className="w-4 h-4 ml-2 !text-[#505050]" />{' '}
                <span>{row?.original?.likes?.length}</span>
              </span>
              <span className="flex items-center space-x-1">
                <SVGAssets.CommentIcons className="w-4 h-4 ml-2 !text-[#505050]" />{' '}
                <span>{row?.original?.comments?.length}</span>
              </span>
            </div>
          );
        }
      },
      {
        header: t('storage'),
        accessorKey: 'size',
        cell: ({ getValue }: any) => {
          const value = getValue();
          let formattedValue = '-';
          if (value) {
            formattedValue = getFileSizeString(value);
          }
          return (
            <span className="text-sm text-gray-500">{formattedValue}</span>
          );
        }
      },
      {
        header: t('lastViewed'),
        accessorKey: 'assetViewDetail',
        cell: ({ row }: any) => {
          const updatedAt = row?.original?.assetViewDetail?.updatedAt;
          let formattedDate: ReactNode = '-';
          if (updatedAt) {
            formattedDate = getMonthDateFormat(updatedAt);
          }
          return <span className="text-sm text-gray-500">{formattedDate}</span>;
        }
      },
      {
        header: '',
        accessorKey: 'actions',
        cell: ({ row }: any) => {
          const asset = row.original;
          if (hideContextMenu) return null;
          return (
            <div className="relative">
              <ContextMenu
                menuItems={contextMenuItemsWithFolder(
                  asset,
                  filterOptions?.accessType
                )}
                iconClass="w-4 h-4 text-gray-500"
                itemContainerClass="!min-w-[180px]"
              />
            </div>
          );
        }
      }
    ];
  }, [selectedPhotos, hideContextMenu]);

  const handleDiaryModalClose = () => {
    setIsAddDiaryModalOpen(false);
  };

  const handleDiarySaved = () => {
    handleDiaryModalClose();
  };

  const selectedStatus = useMemo(() => {
    if (selectedPhotos.length === 0) return undefined;
    const first = (photoArray as any[])?.find(
      (p: any) => p.id === selectedPhotos[0]
    );
    return first?.status as string | undefined;
  }, [selectedPhotos, photoArray]);

  const selectedPhotoRows = useMemo(() => {
    const selectedSet = new Set(selectedPhotos);
    return (
      (photoArray as any[])?.filter((p: any) => selectedSet.has(p.id)) || []
    );
  }, [photoArray, selectedPhotos]);

  const hasSelfCreatedSelectedPending = useMemo(() => {
    if (!currentUserId || selectedStatus !== 'Pending') return false;
    return selectedPhotoRows.some((p: any) => p?.createdBy === currentUserId);
  }, [currentUserId, selectedPhotoRows, selectedStatus]);

  // Wrap context menu items to inject remove-from-folder when in folder context
  const contextMenuItemsWithFolder = (asset: any, accessType?: string) => {
    const items = assetContextMenuItems(asset, accessType);
    if (!folderId) return items;
    const removeItem = {
      id: 'remove-folder',
      label: (
        <ContextMenuItem
          icon={<SVGAssets.MoveTos className="w-4 h-4 rotate-180" />}
          label={t('remove')}
        />
      ),
      onClick: () => {
        setAssetToRemoveFromFolder(asset?.id || '');
        setIsRemoveFromFolderModalOpen(true);
      }
    };
    // Insert before the delete item (last item)
    return [...items.slice(0, -1), removeItem, ...items.slice(-1)];
  };

  const handleBulkMove = () => {
    if (selectedPhotos.length === 0) return;
    setIsBulkMoveModalOpen(true);
  };

  const handleBulkRemoveFromFolder = () => {
    setIsBulkRemoveFromFolderModalOpen(true);
  };

  const confirmBulkRemoveFromFolder = () => {
    RemoveFromFolder({
      folderId,
      body: {
        assetIds: selectedPhotos
      }
    })
      .then(() => {
        setIsBulkRemoveFromFolderModalOpen(false);
        setSelectedPhotos([]);

        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: t('photosPage.moveSuccess')
          }
        });

        assetFiltersHandler();
      })
      .catch((err: any) => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: err?.message || t('photosPage.moveError')
          }
        });
      });
  };

  const selectionActions = useMemo(() => {
    if (isTrashMode) {
      return [
        {
          icon: <SVGAssets.Restore className="w-5 h-5 text-[#49712B]" />,
          onClick: trashBulkOperations.handleBulkRestore,
          title: t('restore')
        },
        {
          icon: <SVGAssets.TrashIcon className="w-5 h-5 text-red-600" />,
          onClick: trashBulkOperations.handleBulkDelete,
          title: t('delete')
        }
      ];
    }

    if (additionalSelectionActions) {
      if (
        selectedStatus === 'Pending' &&
        hasSelfCreatedSelectedPending &&
        !groupId
      ) {
        const filteredActions = additionalSelectionActions.filter(action => {
          const title = `${action?.title || ''}`.toLowerCase();
          return !title.includes('merge') && !title.includes('reject');
        });
        if (filteredActions.length > 0) {
          return filteredActions;
        }
        return [
          {
            icon: <SVGAssets.TrashIcon className="w-5 h-5 text-red-600" />,
            onClick: handleBulkDelete,
            title: t('photosPage.delete')
          }
        ];
      }
      return additionalSelectionActions;
    }

    return [
      ...(selectedStatus === 'Pending'
        ? [
            ...(hasSelfCreatedSelectedPending
              ? [
                  {
                    icon: (
                      <SVGAssets.TrashIcon className="w-5 h-5 text-red-600" />
                    ),
                    onClick: handleBulkDelete,
                    title: t('photosPage.delete')
                  }
                ]
              : []),
            ...(!hasSelfCreatedSelectedPending && canMerge
              ? [
                  {
                    icon: (
                      <SVGAssets.Merge className="w-5 h-5 text-[#7F9D48]" />
                    ),
                    onClick: handleMergeAssets,
                    title: 'Merge'
                  }
                ]
              : []),
            ...(!hasSelfCreatedSelectedPending && canReject
              ? [
                  {
                    icon: <SVGAssets.Reject className="w-5 h-5 text-red-400" />,
                    onClick: handleRejectAssets,
                    title: 'Reject'
                  }
                ]
              : [])
          ]
        : isSharedWithMe
          ? [
              {
                icon: <SVGAssets.TrashIcon className="w-5 h-5 text-red-600" />,
                onClick: handleBulkDelete,
                title: t('photosPage.delete')
              }
            ]
          : !isPending
            ? [
                {
                  icon:
                    filterOptions?.accessType === 'public' ? (
                      <SVGAssets.LockClosedIcon className="w-5 h-5" />
                    ) : (
                      <SVGAssets.LockOpen className="w-5 h-5" />
                    ),
                  onClick: handleBulkPrivate,
                  title:
                    filterOptions?.accessType === 'public'
                      ? t('photosPage.markAsPrivate')
                      : t('photosPage.markAsPublic')
                },

                ...(folderId
                  ? [
                      {
                        icon: <SVGAssets.MoveTos className="rotate-180" />,
                        onClick: handleBulkRemoveFromFolder,
                        title: t('remove')
                      }
                    ]
                  : []),
                filterOptions?.accessType === 'public' && {
                  icon: (
                    <SVGAssets.ShareIcon className="w-5 h-5 text-[#49712B]" />
                  ),
                  onClick: () => setIsBulkShareModalOpen(true),
                  title: t('share.title')
                },
                {
                  icon: <SVGAssets.MoveTos className="w-5 h-5" />,
                  onClick: handleBulkMove,
                  title: t('photosPage.move')
                },
                {
                  icon: (
                    <SVGAssets.TrashIcon className="w-5 h-5 text-red-600" />
                  ),
                  onClick: handleBulkDelete,
                  title: t('photosPage.delete')
                }
              ]?.filter(Boolean)
            : []),
      ...(isPending &&
      !selectedStatus &&
      !hasSelfCreatedSelectedPending &&
      canMerge
        ? [
            {
              icon: <SVGAssets.Merge className="w-5 h-5 text-[#7F9D48]" />,
              onClick: handleMergeAssets,
              title: 'Merge'
            }
          ]
        : []),
      ...(isPending &&
      !selectedStatus &&
      !hasSelfCreatedSelectedPending &&
      canReject
        ? [
            {
              icon: <SVGAssets.Reject className="w-5 h-5 text-red-400" />,
              onClick: handleRejectAssets,
              title: 'Reject'
            }
          ]
        : [])
    ];
  }, [
    additionalSelectionActions,
    groupId,
    canMerge,
    canReject,
    filterOptions?.accessType,
    handleBulkDelete,
    handleBulkMove,
    handleBulkPrivate,
    hasSelfCreatedSelectedPending,
    isPending,
    isSharedWithMe,
    isTrashMode,
    selectedStatus,
    t,
    trashBulkOperations.handleBulkDelete,
    trashBulkOperations.handleBulkRestore
  ]);

  const extraPayload = useMemo(() => {
    const payload: any = {};
    if (groupId) {
      payload.groupId = groupId;
      payload.module = 'group';
    }
    return payload;
  }, [groupId]);

  if (
    photosCount === 0 &&
    typeof emptyStateComponent !== 'undefined' &&
    !isLoading
  ) {
    return <>{emptyStateComponent}</>;
  }

  return (
    <div className="w-full">
      <AppLoader isLoading={isLoading} />
      {/* Selection Bar - for both Photos and Folders */}
      {isShareModalOpen && (
        <ShareModalComponent
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          link={`${window.location.origin}/photos/media/${selectedAsset?.id}`}
          assetIds={[selectedAsset?.id]}
          shareType="asset"
          onMessage={(type, message) =>
            showToast({ type, isAlert: true, alertProps: { type, message } })
          }
        />
      )}
      {isBulkShareModalOpen && (
        <ShareModalComponent
          isOpen={isBulkShareModalOpen}
          onClose={() => setIsBulkShareModalOpen(false)}
          link={
            selectedPhotos.length === 1
              ? `${window.location.origin}/photos/media/${photoArray?.find((p: any) => p.id === selectedPhotos[0])?.id}`
              : `${window.location.origin}/sharing?type=media`
          }
          assetIds={selectedPhotos}
          shareType="asset"
          onMessage={(type, message) => {
            showToast({ type, isAlert: true, alertProps: { type, message } });
            if (type === 'success') setSelectedPhotos([]);
          }}
        />
      )}
      {selectedPhotos.length > 0 && !isFavorites && (
        <SelectionBar
          selectedCount={
            isTrashMode
              ? trashBulkOperations.totalSelectedCount
              : selectedPhotos.length
          }
          itemType="photo"
          onDeselectAll={() => {
            setSelectedPhotos([]);
            if (isTrashMode) {
              setSelectedFolders([]);
            }
          }}
          actions={selectionActions}
        />
      )}

      {(isPending || isMergeConfirmOpen || isRejectConfirmOpen) && (
        <>
          <ConfirmationBox
            isOpen={isMergeConfirmOpen}
            onClose={() => setIsMergeConfirmOpen(false)}
            onCancel={() => setIsMergeConfirmOpen(false)}
            onConfirm={handleMergeConfirm}
            headerTitle="Merge Contributions"
            title="Do you want to merge?"
            description={`This will merge ${selectedPhotos.length} selected photo(s).`}
            icon={<SVGAssets.Merge className="w-16 h-16 text-[#7F9D48]" />}
            confirmText="Yes, Merge"
            cancelText="Cancel"
            isLoading={isMerging}
          />
          <ConfirmationBox
            isOpen={isRejectConfirmOpen}
            onClose={() => setIsRejectConfirmOpen(false)}
            onCancel={() => setIsRejectConfirmOpen(false)}
            onConfirm={handleRejectConfirm}
            headerTitle="Reject Contributions"
            title="Reject selected photos?"
            description={`This will reject ${selectedPhotos.length} selected photo(s). This action cannot be undone.`}
            icon={<SVGAssets.Reject className="w-16 h-16 text-red-400" />}
            confirmText="Yes, Reject"
            cancelText="Cancel"
            isLoading={isRejecting}
          />
        </>
      )}

      {/* Filter Section */}
      {!isWhoIsThis && (
        <>
          {isFilterEnabled && (
            <div className="mb-2 w-full">
              {isEnableAdd && (
                <div className="flex items-center justify-end mb-3">
                  <Button
                    btnStyle="primary"
                    className="!text-sm"
                    onClick={handleAddMedia}>
                    {t('uploadPhoto')}
                  </Button>
                </div>
              )}
              <div className="flex justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {t('photosPage.filterBy')}
                  </h3>
                </div>
                <div>
                  <Button
                    btnStyle="link"
                    onClick={() => {
                      setFilterOptions({
                        search: '',
                        accessType: 'public',
                        ...initialFilterOptions
                      });
                    }}
                    className="text-sm font-semibold text-[#49712B] hover:underline">
                    {t('clearFilters')}
                  </Button>
                </div>
              </div>

              <Filters
                setFilterOptions={setFilterOptions}
                selectedView={selectedView}
                onViewChange={setSelectedView}
                filterOptions={filterOptions}
                module={
                  module ? module : isFromPeopleDetail ? 'people' : 'assets'
                }
                peopleId={initialFilterOptions?.peopleId}
                isUpdateFilters={isUpdateFilters}
                setIsUpdateFilters={setIsUpdateFilters}
                folderId={folderId}
                isExcludeFromFilter={
                  isSharedWithMeSource || isExcludeFromFilter
                }
                tab={isSharedWithMeSource ? 'sharedWithMe' : undefined}
                isInsidePeople={isFromPeopleDetail ? true : undefined}
                isSharedWithMe={isSharedWithMe ? true : undefined}
                isMyFiles={isMyFiles ? true : undefined}
                extraPayload={extraPayload}
              />
              <SearchInput
                className="mt-0"
                value={search || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(e.target.value);
                }}
                onCameraClick={() => {
                  console.log('test');
                }}
                isLoading={isLoading}
              />
            </div>
          )}
        </>
      )}

      {/* Photos Section */}
      <div className="overflow-visible">
        {/* Photos heading and Access Dropdown */}
        <div className="flex items-center justify-between mb-4">
          {!isFavorites && (
            <h2 className="text-lg font-semibold text-gray-800">
              {t('photos')}
            </h2>
          )}
          {!isWhoIsThis && (
            <>
              {isFilterEnabled &&
                !hidePrivatePublicFilter &&
                isPrivateDataVisible && (
                  <PrivatePublicFilter
                    accessType={filterOptions?.accessType}
                    setFilterOptions={setFilterOptions}
                  />
                )}
            </>
          )}
        </div>

        {false && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={handleSelectAll}
                className="text-sm text-[#7F9D48] hover:underline"
                btnStyle="no-outline">
                {selectedPhotos.length === photosCount
                  ? t('photosPage.deselectAll')
                  : t('photosPage.selectAll')}
              </Button>
              <div className="flex gap-1 border border-gray-300 rounded-md p-1">
                <Button
                  btnStyle="link"
                  onClick={() => setSelectedView('grid')}
                  className={`p-1.5 rounded ${selectedView === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}>
                  <SVGAssets.GridIcon className="w-4 h-4" />
                </Button>
                <Button
                  btnStyle="link"
                  onClick={() => setSelectedView('list')}
                  className={`p-1.5 rounded ${selectedView === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}>
                  <SVGAssets.ListIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {photos?.count > 0 ? (
          <div>
            {selectedView === 'grid' ? (
              <PhotoGrid
                photos={photoArray}
                selectedPhotos={selectedPhotos}
                onPhotoSelect={handlePhotoSelect}
                onToggleFavorite={handleToggleFavorite}
                onRowClick={handleRowClick}
                contextMenuItems={contextMenuItemsWithFolder}
                accessType={filterOptions?.accessType}
                isFromTrashPage={folderDetails?.isFromTrash}
                isReadOnlyTrashFolder={
                  !!folderDetails?.isFromTrash || hideContextMenu
                }
                hideContextMenu={groupId ? true : false}
                hideFavorite={
                  (hideContextMenu && !showFavoriteWhenContextMenuHidden) ||
                  isContributor
                }
                // selectedStatus={selectedStatus}
                // onPendingMerge={canMerge ? handlePendingMerge : undefined}
                // onPendingReject={canReject ? handlePendingReject : undefined}
                currentUserId={currentUserId}
                groupId={groupId}
                // isOwnerAccess={isOwnerAccess}
                hideCheckbox={isContributor || hideContextMenu}
              />
            ) : (
              <div className="w-full overflow-visible">
                <Table
                  tableColumns={columns}
                  tableData={photoArray}
                  className="w-full"
                  onRowClick={handleRowClick}
                />
              </div>
            )}
            {/* Pagination - only show in list view */}
            {selectedView === 'list' && (
              <Pagination
                totalCount={totalCount}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={(page: number) => {
                  setCurrentPage(page);
                }}
              />
            )}
          </div>
        ) : (
          <FilterSearchEmpty />
        )}
      </div>

      {/* Delete Asset Confirmation */}
      <ConfirmationModal
        isOpen={isDeleteAssetConfirm}
        onClose={() => {
          setIsDeleteAssetConfirm(false);
          setSelectedAsset(null);
        }}
        onConfirm={() => {
          setIsDeleteAssetConfirm(false);
          deleteAssetHandler(selectedAsset?.id);
        }}
        variant="delete"
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmationModal
        isOpen={isBulkDeleteConfirm}
        onClose={() => setIsBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        variant="delete"
      />

      {/* Move Photo Modal */}
      <MovePhotoModal
        isOpen={isMovePhotoModalOpen}
        onClose={() => {
          setIsMovePhotoModalOpen(false);
        }}
        assetId={selectedAsset?.id}
        onSuccess={() => {
          showToast({
            type: 'success',
            isAlert: true,
            alertProps: {
              type: 'success',
              message: t('photosPage.moveSuccess')
            }
          });
          assetFiltersHandler();
        }}
        onError={() => {
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: t('photosPage.moveError')
            }
          });
        }}
      />

      {/* Bulk Move Photo Modal */}
      <MovePhotoModal
        isOpen={isBulkMoveModalOpen}
        onClose={() => {
          setIsBulkMoveModalOpen(false);
        }}
        assetIds={selectedPhotos}
        onSuccess={() => {
          showToast({
            type: 'success',
            isAlert: true,
            alertProps: {
              type: 'success',
              message: t('photosPage.moveSuccess')
            }
          });
          setSelectedPhotos([]);
          setIsBulkMoveModalOpen(false);
          assetFiltersHandler();
        }}
        onError={() => {
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: t('photosPage.moveError')
            }
          });
        }}
      />

      {/* Media Info Sidebar */}
      <MediaInfoSidebar
        isOpen={isInfoSidebarOpen}
        onClose={() => {
          setIsInfoSidebarOpen(false);
        }}
        assetId={selectedAsset?.id}
        setIsUpdateFilters={setIsUpdateFilters}
      />

      <AddDiaryModal
        isOpen={isAddDiaryModalOpen}
        onClose={handleDiaryModalClose}
        onSave={handleDiarySaved}
        assetId={selectedAsset?.id}
        editingDiary={null}
      />

      {/* Remove from Folder Confirmation */}
      <ConfirmationBox
        isOpen={isRemoveFromFolderModalOpen}
        onCancel={() => {
          setIsRemoveFromFolderModalOpen(false);
          setAssetToRemoveFromFolder('');
        }}
        onClose={() => {
          setIsRemoveFromFolderModalOpen(false);
          setAssetToRemoveFromFolder('');
        }}
        onConfirm={() => {
          removeFromFolderHandler(assetToRemoveFromFolder);
        }}
        headerTitle={t('removeFromFolder')}
        title={t('removeFromFolderDesc')}
        confirmText={t('removeFromFolder')}
        cancelText={t('cancel')}
      />

      {/* Bulk Delete Confirmation */}
      {/* <ConfirmationModal
        isOpen={isBulkPrivateConfirm}
        onClose={() => setIsBulkPrivateConfirm(false)}
        onConfirm={confirmBulkPrivate}
        variant="delete"
      /> */}

      {/* Bulk Private Confirmation */}
      {isBulkPrivateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">
                {filterOptions?.accessType === 'public'
                  ? t('photosPage.markAsPrivate')
                  : t('photosPage.markAsPublic')}
              </h2>
              <Button
                onClick={() => setIsBulkPrivateConfirm(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Close"
                btnStyle="no-outline">
                ×
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 text-center">
              <div className="mb-4 flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  {filterOptions?.accessType === 'public' ? (
                    <SVGAssets.LockClosedIcon className="w-12 h-12 text-green-600" />
                  ) : (
                    <SVGAssets.LockOpen className="w-12 h-12 text-green-600" />
                  )}
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {filterOptions?.accessType === 'public'
                  ? t('photosPage.bulkPrivateTitle')
                  : t('photosPage.bulkPublicTitle')}
              </h3>
              <p className="text-sm text-gray-500">
                {filterOptions?.accessType === 'public'
                  ? t('photosPage.bulkPrivateMessage')
                  : t('photosPage.bulkPublicMessage')}
              </p>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t">
              <Button
                onClick={() => setIsBulkPrivateConfirm(false)}
                btnStyle="no-outline"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                {t('cancel')}
              </Button>
              <Button
                onClick={confirmBulkPrivate}
                btnStyle="no-outline"
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                {filterOptions?.accessType === 'public'
                  ? t('photosPage.yesMarkPrivate')
                  : t('photosPage.yesMarkPublic')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationBox
        isOpen={isBulkRemoveFromFolderModalOpen}
        onCancel={() => setIsBulkRemoveFromFolderModalOpen(false)}
        onClose={() => setIsBulkRemoveFromFolderModalOpen(false)}
        onConfirm={confirmBulkRemoveFromFolder}
        headerTitle={t('removeFromFolder')}
        title={t('removeFromFolderDesc')}
        confirmText={t('removeFromFolder')}
        cancelText={t('cancel')}
      />

      {/* Restore Asset Confirmation (for trash mode) */}
      {isTrashMode && (
        <>
          <ConfirmationBox
            isOpen={isAssetRestoreConfirm}
            onCancel={() => {
              setIsAssetRestoreConfirm(false);
              setRestoreAssetId('');
            }}
            onClose={() => {
              setIsAssetRestoreConfirm(false);
              setRestoreAssetId('');
            }}
            onConfirm={() => {
              restoreAssetHandler(restoreAssetId);
            }}
            headerTitle={t('recover')}
            title={t('recoverDesc')}
            confirmText={t('recover')}
            cancelText={t('cancel')}
          />

          {/* Bulk Restore Confirmation */}
          <ConfirmationBox
            isOpen={trashBulkOperations.isBulkRestoreConfirm}
            onCancel={() => trashBulkOperations.setIsBulkRestoreConfirm(false)}
            onClose={() => trashBulkOperations.setIsBulkRestoreConfirm(false)}
            onConfirm={trashBulkOperations.confirmBulkRestore}
            headerTitle={t('bulkRestore')}
            title={t('bulkRestoreDesc')}
            confirmText={t('restore')}
            cancelText={t('cancel')}
          />

          {/* Bulk Delete Confirmation */}
          <ConfirmationModal
            isOpen={trashBulkOperations.isBulkDeleteConfirm}
            onClose={() => trashBulkOperations.setIsBulkDeleteConfirm(false)}
            onConfirm={trashBulkOperations.confirmBulkDelete}
            variant="delete"
          />
        </>
      )}
    </div>
  );
};
